import React, { useRef, useState } from "react";
import ReactCrop, { Crop } from 'react-image-crop';
import { observer } from 'mobx-react-lite';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import 'react-image-crop/dist/ReactCrop.css';

const UploadImageDialog = observer(({ store }: { store: any }) => {
    const [crop, setCrop] = useState<Crop>({
        unit: 'px', // Use pixels for the crop area
        width: 512, // Default width of the crop area
        height: 512, // Default height of the crop area
        x: 0, // Default x position
        y: 0, // Default y position
    });
    const [cropDialogVisible, setCropDialogVisible] = useState(false);
    const [imageSrc, setImageSrc] = useState<string | null>(null);
    const [croppedImage, setCroppedImage] = useState<string | null>(null);
    const imageRef = useRef<HTMLImageElement | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const useImage = croppedImage || store.imageBlob;

    console.log('useImage', useImage);
    const handleImageLoaded = () => {
        if (imageRef.current) {
            const image = imageRef.current;

            // Get the rendered size of the image
            const renderedWidth = image.width;
            const renderedHeight = image.height;

            // Calculate the default crop size (max 512px, 1:1 aspect ratio)
            const size = Math.min(512, renderedWidth, renderedHeight);
            const x = (renderedWidth - size) / 2; // Center horizontally
            const y = (renderedHeight - size) / 2; // Center vertically

            setCrop({
                unit: 'px',
                width: size,
                height: size,
                x,
                y,
            });
        }
    };

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            const reader = new FileReader();
            reader.onload = () => setImageSrc(reader.result as string);
            reader.readAsDataURL(e.target.files[0]);
            setCropDialogVisible(true);

            store.setImage(e.target.files[0]);
        }
    };

    const handleCropComplete = (crop: Crop) => {
        if (imageSrc && crop.width && crop.height && imageRef.current) {
            const canvas = document.createElement('canvas');
            const image = imageRef.current;

            // Berechne das Skalierungsverhältnis
            const scaleX = image.naturalWidth / image.width;
            const scaleY = image.naturalHeight / image.height;

            // Originalgröße des zugeschnittenen Bereichs
            const croppedWidth = crop.width * scaleX;
            const croppedHeight = crop.height * scaleY;

            // Zielgröße (maximal 512x512 Pixel)
            const maxDimension = 512;
            const aspectRatio = croppedWidth / croppedHeight;

            let targetWidth = croppedWidth;
            let targetHeight = croppedHeight;

            if (croppedWidth > maxDimension || croppedHeight > maxDimension) {
                if (aspectRatio > 1) {
                    // Breiter als hoch
                    targetWidth = maxDimension;
                    targetHeight = maxDimension / aspectRatio;
                } else {
                    // Höher als breit
                    targetHeight = maxDimension;
                    targetWidth = maxDimension * aspectRatio;
                }
            }

            // Setze die Zielgröße des Canvas
            canvas.width = targetWidth;
            canvas.height = targetHeight;

            const ctx = canvas.getContext('2d');
            if (ctx) {
                // Zeichne das zugeschnittene Bild auf das Canvas
                ctx.drawImage(
                    image,
                    crop.x * scaleX,
                    crop.y * scaleY,
                    croppedWidth,
                    croppedHeight,
                    0,
                    0,
                    targetWidth,
                    targetHeight
                );

                // Konvertiere das Canvas in ein Blob
                canvas.toBlob((blob) => {
                    if (blob) {
                        const croppedImageUrl = URL.createObjectURL(blob);
                        setCroppedImage(croppedImageUrl); // Setze die Vorschau des zugeschnittenen Bildes
                        store.setImage(blob); // Speichere das Blob im Store
                        setCropDialogVisible(false); // Schließe den Dialog
                    }
                }, 'image/jpeg', 0.8); // Optional: Qualität auf 90% setzen
            }
        }
    };

    return <>
        <div style={{ marginTop: '1rem', marginBottom: '1rem', textAlign: 'center' }}>
            <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                ref={fileInputRef}
                style={{ display: 'none' }}
            />
            {useImage ? (
                <img
                    src={useImage}
                    alt="Cropped"
                    style={{
                        width: '150px',
                        height: '150px',
                        objectFit: 'cover',
                        cursor: 'pointer',
                        borderRadius: '15%'
                    }}
                    onClick={() => fileInputRef.current?.click()}
                />
            ) : (
                <div>
                    <Button
                        icon="pi pi-upload"
                        className="p-button-rounded p-button-secondary"
                        onClick={() => fileInputRef.current?.click()}
                        style={{ width: '4rem', height: '4rem' }}
                    />
                    <div style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: '#6c757d' }}>
                        Bild hinzufügen
                    </div>
                </div>
            )}

        </div>
        <Dialog
            visible={cropDialogVisible}
            modal
            header={'Bild zuschneiden'}
            footer={
                <Button label="Speichern" onClick={() => {
                    if (crop) {
                        handleCropComplete(crop);
                    }
                }} />
            }
            onHide={() => setCropDialogVisible(false)}
        >
            {imageSrc && (
                <ReactCrop
                    crop={crop}
                    onChange={(newCrop: any) => setCrop(newCrop)}
                    aspect={1 / 1}
                >
                    <img
                        ref={imageRef}
                        src={imageSrc}
                        alt={'cropped-image'}
                        style={{ maxWidth: '512px' }}
                        onLoad={handleImageLoaded} // Set default crop when the image is loaded
                    />
                </ReactCrop>
            )}
        </Dialog>
    </>
});

export default UploadImageDialog;